/**
 * Which school scope a CLS-DK product / package page must bind onto the cart
 * right BEFORE an add. The API prices every line by the CART's school + shop +
 * class (class price overrides, class-disabled options), so a cart that knows
 * the school but not the class charges the shop's default price although the
 * page showed the class price.
 *
 *  - cart without a school → the full identified scope: school, class and — when
 *    `includeShop` — the shop (bare pages; on a school-shop product URL the API
 *    binds the shop from the line itself);
 *  - cart already bound to the SAME school, but WITHOUT a class and still EMPTY →
 *    school + class (the shop it has is kept). This is the cart a product URL
 *    opened first leaves behind: the deep-link bind only knew the school;
 *  - anything else → null. A cart that has a class, belongs to another school or
 *    already holds lines is never touched here: the API starts a NEW cart on any
 *    class change, which would silently drop those lines.
 *
 * @param {object}  args
 * @param {{id: number|string, class?: string|null}|null} args.cartSchool      the cart's current binding
 * @param {number}  args.itemCount       lines in the cart
 * @param {object|null} args.identification  fresh stored selection { school: {id}, season: {id}, class }
 * @param {boolean} [args.includeShop]   send the selection's shop for a school-less cart
 * @returns {{school_id: number|string, shop_id?: number|string, school_class?: string}|null}
 */
export function cartScopeToBind({ cartSchool, itemCount = 0, identification, includeShop = false }) {
  const schoolId = identification?.school?.id;
  if (schoolId == null) return null;
  const cls = identification.class || null;

  if (cartSchool?.id == null) {
    return {
      school_id: schoolId,
      ...(includeShop && identification.season?.id ? { shop_id: identification.season.id } : {}),
      ...(cls ? { school_class: cls } : {}),
    };
  }

  const sameSchool = String(cartSchool.id) === String(schoolId);
  if (sameSchool && !cartSchool.class && cls && itemCount === 0) {
    return { school_id: schoolId, school_class: cls };
  }
  return null;
}

/**
 * The class to send with the product page's deep-link bind (a product URL is
 * the visitor's first page and the cart has no school yet): the stored class,
 * when the stored selection belongs to the school of that URL.
 */
export function storedClassForSchool(selection, schoolId) {
  if (schoolId == null || selection?.school?.id == null) return null;
  return String(selection.school.id) === String(schoolId) ? (selection.class || null) : null;
}
